import { Image3D } from '~/domain/patient/image3d';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useRunningTasks } from '~/hooks/useRunningTasks';
import { downloadImageToDisk } from '~/utils/io/files/downloadDicom';

/** The download of an image as a DICOM series. */
export interface ImageDownload {
    /** Starts the download; it reports its own failure to the user. */
    readonly download: () => void;
    /** Whether a download of this image is running. */
    readonly busy: boolean;
    /** Why the image cannot be downloaded now; undefined while it can. */
    readonly refusal: string | undefined;
}

/**
 * The download of `image`, undefined without one.
 *
 * Refused, with the reason, while the image is incomplete: it would write a
 * DICOM series holding only the slices that arrived, a file set that looks
 * like a complete study and is not one. A running download shows as running
 * on its own image only, also after another image took its place.
 */
export function useImageDownload(
    image: Image3D | undefined
): ImageDownload | undefined {
    const gate = useImageCompletenessGate(image);
    const downloads = useRunningTasks();

    if (!image) {
        return undefined;
    }
    return {
        download: () =>
            downloads.run(image.seriesUid, () => downloadImageToDisk(image)),
        busy: downloads.isRunning(image.seriesUid),
        refusal: gate.reason,
    };
}
